// Activate this computer.
//
// Shown when an operator signs in on a machine with no stored WeighBridge ID and
// License Key, and from the "Change" link when a licence has been renewed.
//
// ─── Two labelled fields, and nothing else ─────────────────────────────
// No placeholders. A realistic-looking sample in an empty input reads as a VALUE
// ALREADY ENTERED — operators either try to sign in with it or delete it character by
// character, so an input that is in fact empty looks pre-filled and wrong.
//
// No hint prose either. The labels already say what each field is, and the operator is
// copying two values off a certificate in their hand; a paragraph explaining what a
// licence key looks like is clutter in front of somebody who is holding one. What
// remains is validation, which speaks only when something is actually wrong.
//
// ─── Why the username and password are NOT here ────────────────────────
// They were typed on the login form behind this dialog. Activation uses all four
// values, but only two are asked for here, so the operator is never asked twice for
// something they have already given. The screen runs them together; see screens/Login.
import { useEffect } from 'react';
import { Modal, Form, Input, Alert, Button } from 'antd';
import { IdcardOutlined, KeyOutlined, SafetyCertificateOutlined } from '@ant-design/icons';

export interface ActivationValues {
    weighbridge_id: string;
    license_key: string;
}

interface Props {
    open: boolean;
    loading: boolean;
    error: string | null;
    /**
     * True when this computer is ALREADY activated and the operator is replacing a
     * renewed or expired licence.
     *
     * Worth distinguishing: they are different actions, and one heading served neither.
     * "Activate" in front of somebody whose licence just expired reads as though the
     * software had forgotten them — and the renewal case has a fact the first-run case
     * does not, which is that the key on this machine is about to be replaced.
     */
    reactivating?: boolean;
    /**
     * The WeighBridge ID this computer currently holds, prefilled when reactivating.
     *
     * Shown so the operator can SEE what they are changing away from. Without it the
     * dialog opens blank and asks for an id it already knows, which reads as "the
     * software has forgotten this machine" - and on the licence-renewal path the id is
     * usually not the thing changing, only the key.
     *
     * Never the licence key: that field always starts empty, so a renewal cannot be
     * completed by pressing Enter over a stale value.
     */
    currentWeighbridgeId?: string | null;
    onCancel: () => void;
    onSubmit: (values: ActivationValues) => void;
}

export default function ActivationDialog({
    open,
    loading,
    error,
    reactivating = false,
    currentWeighbridgeId = null,
    onCancel,
    onSubmit,
}: Props) {
    const [form] = Form.useForm<ActivationValues>();

    // ─── Belt and braces against autofill ───────────────────────────────
    // The autoComplete attributes below are hints, and browsers and password
    // managers honour them inconsistently. Autofill also lands AFTER mount, so a
    // field that is empty when the dialog opens can be populated a tick later.
    //
    // This clears both fields once the dialog is shown, which does not depend on
    // any heuristic. It runs before the operator has typed anything, so nothing of
    // theirs is ever discarded.
    useEffect(() => {
        if (!open) return;
        const id = setTimeout(() => {
            form.resetFields();
            // Set AFTER the reset, and in the same tick, so the prefill is not the thing
            // that gets cleared. The key is deliberately left empty - see the prop.
            if (reactivating && currentWeighbridgeId) {
                form.setFieldsValue({ weighbridge_id: currentWeighbridgeId });
            }
        }, 0);
        return () => clearTimeout(id);
    }, [open, form, reactivating, currentWeighbridgeId]);

    return (
        <Modal
            open={open}
            title={
                <span>
                    <SafetyCertificateOutlined style={{ marginRight: 8 }} />
                    {reactivating ? 'Update licence' : 'Activate this computer'}
                </span>
            }
            onCancel={onCancel}
            // Not dismissable mid-request: a click on the mask while activation is in
            // flight would leave the operator on a screen that then changes under them.
            maskClosable={!loading}
            closable={!loading}
            footer={null}
            width={460}
            destroyOnHidden
        >
            <Form form={form} layout="vertical" requiredMark={false} onFinish={onSubmit}>
                <Form.Item
                    label="WeighBridge ID"
                    name="weighbridge_id"
                    // Digits only, and deliberately NOT length-checked. A leading zero is
                    // significant and ids differ in width, so there is nothing to
                    // validate but the character set — the value is passed through
                    // exactly as printed.
                    rules={[
                        { required: true, message: 'WeighBridge ID is required' },
                        {
                            pattern: /^[0-9]+$/,
                            message:
                                'Digits only — enter it exactly as printed, including any leading zero.',
                        },
                    ]}
                >
                    <Input
                        autoFocus
                        prefix={<IdcardOutlined />}
                        // ─── Keeping the password manager out ───────────
                        // This form contains a password-type input, so Chrome
                        // classifies the whole dialog as a SIGN-IN form and fills
                        // the first text field with the saved username - which put
                        // "admin" in the WeighBridge ID box and the saved password
                        // in the License Key box, both failing validation in front
                        // of an operator who had typed neither.
                        //
                        // autoComplete="off" is a hint Chrome ignores for exactly
                        // this case. What it does respect is "new-password" on the
                        // credential field (below), which stops the form reading as
                        // a sign-in; role="presentation" is the long-standing
                        // heuristic breaker for the text field.
                        autoComplete="off"
                        role="presentation"
                        data-lpignore="true"
                        data-1p-ignore
                        size="large"
                        disabled={loading}
                        inputMode="numeric"
                    />
                </Form.Item>

                <Form.Item
                    label="License Key"
                    name="license_key"
                    rules={[
                        { required: true, message: 'License Key is required' },
                        {
                            // Checked here as well as server-side so a mistyped key is
                            // caught before it costs a round trip — and so the operator
                            // is told "that is 15 digits" rather than a generic refusal,
                            // which reads as though the licence itself were the problem.
                            validator: (_r, value: string | undefined) =>
                                !value || /^[0-9]{16}$/.test(value.replace(/[\s-]/g, ''))
                                    ? Promise.resolve()
                                    : Promise.reject(
                                          new Error(
                                              'A License Key is 16 digits. Check it against the certificate and try again.',
                                          ),
                                      ),
                        },
                    ]}
                >
                    {/* Input.Password: a credential should not sit legible on a
                        counter-top screen.

                        Hyphens and spaces are optional, and that holds on BOTH sides —
                        the server normalises separators away before hashing, so the same
                        typed value works whether it was copied with the grouping or
                        without it. */}
                    <Input.Password
                        prefix={<KeyOutlined />}
                        // "new-password", not "off": this is the signal Chrome
                        // actually honours. It also stops the browser OFFERING to
                        // save the licence key as a password, which it otherwise
                        // does on every activation.
                        autoComplete="new-password"
                        data-lpignore="true"
                        data-1p-ignore
                        size="large"
                        disabled={loading}
                        inputMode="numeric"
                    />
                </Form.Item>

                {error && (
                    <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />
                )}

                <Button type="primary" htmlType="submit" block size="large" loading={loading}>
                    {reactivating ? 'Update licence' : 'Activate'}
                </Button>
            </Form>
        </Modal>
    );
}
